import React from 'react';
import { MessageSquare, Phone, Mail, Info, Check, ArrowRight } from 'lucide-react';
import { openChat } from '../../../utils/cn';

// /support — "Reach a real roofer." section, rebuilt 2026-05-24 from
// the user's mockup. Replaces the legacy KnowledgeBase search/category
// grid. 3 channel cards: Live Chat (cyan, featured) / Call a Roofer
// (green) / Email Us (purple). Each card has a tinted info panel that
// honestly describes the channel (AI vs real human vs async).
//
// File kept named KnowledgeBase.jsx so the existing support/index.jsx
// import doesn't need to change. Content is no longer a knowledge-base
// search — it's the "still need help?" channel selector.

const PHONE_TEL = '9379108791';
const PHONE_DISPLAY = '937-910-8791';

const scrollToContactForm = () => {
  const el = document.getElementById('contact-form');
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const CHANNELS = [
  {
    id: 'chat',
    title: 'Live Chat',
    badge: { label: 'Online now', tone: 'green' },
    body: "The fastest way to get answers to common questions — pricing, scheduling, what's included, how the process works.",
    Icon: MessageSquare,
    accent: '#07B0E9', // brand cyan
    info: {
      tint: 'rgba(7, 176, 233, 0.08)',
      border: 'rgba(7, 176, 233, 0.30)',
      icon: 'info',
      text: 'Our chat assistant is AI, available 24/7. For complex or situational questions, the phone is better.',
      color: '#0a6eb8',
    },
    cta: {
      label: 'Start chat',
      onClick: () => openChat(),
      background: '#07B0E9',
    },
    featured: true, // gets the cyan outer border
  },
  {
    id: 'phone',
    title: 'Call a Roofer',
    badge: { label: 'Business hours', tone: 'amber' },
    body: 'For anything specific or nuanced — your roof situation, an active project, insurance questions. A real roofer picks up, not a call center.',
    Icon: Phone,
    accent: '#10B981', // green
    info: {
      tint: 'rgba(16, 185, 129, 0.08)',
      border: 'rgba(16, 185, 129, 0.30)',
      icon: 'check',
      text: "A real human who's actually been on a roof — available during business hours, Mon–Sat.",
      color: '#047857',
    },
    cta: {
      label: `Call ${PHONE_DISPLAY}`,
      href: `tel:${PHONE_TEL}`,
      background: '#047857', // dark green per the mockup CTA
    },
    featured: false,
  },
  {
    id: 'email',
    title: 'Email Us',
    badge: { label: 'Always open', tone: 'green' },
    body: "For detailed questions where you want documentation, or anything that's better with attachments — photos, estimates, paperwork.",
    Icon: Mail,
    accent: '#8B5CF6', // purple
    info: {
      tint: 'rgba(139, 92, 246, 0.08)',
      border: 'rgba(139, 92, 246, 0.30)',
      icon: 'info',
      text: 'Use the form below or email us directly. We reply within one business day.',
      color: '#6d28d9',
    },
    cta: {
      label: 'Send a message',
      onClick: scrollToContactForm,
      background: '#8B5CF6',
    },
    featured: false,
  },
];

const Badge = ({ label, tone }) => {
  const styles = {
    green: { bg: 'rgba(16, 185, 129, 0.14)', color: '#047857' },
    amber: { bg: 'rgba(245, 158, 11, 0.16)', color: '#92400e' },
  };
  const s = styles[tone] || styles.green;
  return (
    <span
      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap"
      style={{ background: s.bg, color: s.color }}
    >
      {label}
    </span>
  );
};

const ChannelCard = ({ channel }) => {
  const { title, badge, body, Icon: ChannelIcon, accent, info, cta, featured } = channel;

  const handleCtaClick = () => {
    if (cta.onClick) cta.onClick();
    else if (cta.href) window.location.href = cta.href;
  };

  return (
    <article
      className="rounded-2xl bg-white flex flex-col p-7 lg:p-8 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl"
      style={{
        border: featured ? `2px solid ${accent}` : '1px solid #e5ebf0',
        boxShadow: featured
          ? `0 12px 32px ${accent}1F, 0 0 0 4px ${accent}0A`
          : '0 1px 3px rgba(15, 23, 42, 0.04), 0 8px 24px rgba(15, 23, 42, 0.06)',
      }}
    >
      {/* Icon badge — colored rounded square */}
      <div
        className="flex items-center justify-center rounded-xl mb-6"
        style={{
          width: '52px',
          height: '52px',
          background: accent,
          boxShadow: `0 10px 24px ${accent}33`,
        }}
        aria-hidden="true"
      >
        <ChannelIcon size={24} strokeWidth={2.2} color="white" />
      </div>

      {/* Title + status badge */}
      <div className="flex items-center gap-2.5 mb-3 flex-wrap">
        <h3
          className="font-black"
          style={{
            fontSize: 'clamp(20px, 1.6vw, 22px)',
            color: '#0a1628',
            fontFamily: "'Poppins', 'Montserrat', sans-serif",
            letterSpacing: '-0.015em',
          }}
        >
          {title}
        </h3>
        <Badge label={badge.label} tone={badge.tone} />
      </div>

      {/* Body */}
      <p className="text-[14.5px] lg:text-[15px] text-gray-500 leading-relaxed mb-6">
        {body}
      </p>

      {/* Info panel — tinted with the channel color */}
      <div
        className="rounded-xl p-4 mb-6 flex gap-2.5 items-start"
        style={{
          background: info.tint,
          border: `1px solid ${info.border}`,
        }}
      >
        {info.icon === 'check' ? (
          <Check
            size={16}
            strokeWidth={2.8}
            style={{ color: info.color }}
            className="flex-shrink-0 mt-0.5"
            aria-hidden="true"
          />
        ) : (
          <Info
            size={16}
            strokeWidth={2.4}
            style={{ color: info.color }}
            className="flex-shrink-0 mt-0.5"
            aria-hidden="true"
          />
        )}
        <p
          className="text-[13.5px] font-semibold leading-snug"
          style={{ color: info.color }}
        >
          {info.text}
        </p>
      </div>

      {/* CTA — full-width rounded-full button in the channel color */}
      <button
        type="button"
        onClick={handleCtaClick}
        className="group mt-auto w-full inline-flex items-center justify-center gap-2 font-bold text-white px-5 py-3.5 rounded-full text-[15px] transition-transform duration-200 hover:-translate-y-0.5"
        style={{
          background: cta.background,
          boxShadow: `0 10px 22px ${cta.background}40`,
        }}
      >
        {cta.label}
        <ArrowRight
          size={16}
          strokeWidth={2.6}
          className="transition-transform duration-200 group-hover:translate-x-1"
          aria-hidden="true"
        />
      </button>
    </article>
  );
};

const KnowledgeBase = () => (
  <section className="py-16 lg:py-24" style={{ background: '#F8FAFC' }}>
    <div className="max-w-6xl mx-auto px-5 lg:px-8">
      {/* Section header */}
      <div className="text-center mb-12 lg:mb-16">
        <div className="mb-5 flex justify-center">
          <div
            className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.14em]"
            style={{
              background: 'rgba(7, 176, 233, 0.10)',
              border: '1px solid rgba(7, 176, 233, 0.30)',
              color: '#0a6eb8',
            }}
          >
            Still Need Help?
          </div>
        </div>
        <h2
          className="font-black leading-[1.05] tracking-tight mb-4 mx-auto"
          style={{
            fontSize: 'clamp(32px, 4.5vw, 52px)',
            fontFamily: "'Poppins', 'Montserrat', sans-serif",
            letterSpacing: '-0.025em',
            color: '#0a1628',
            maxWidth: '760px',
          }}
        >
          Reach a{' '}
          <span style={{ color: '#07B0E9' }}>real roofer.</span>
        </h2>
        <p className="text-base lg:text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed">
          Three ways to get in touch — and we're honest about which is which.
        </p>
      </div>

      {/* 3-card grid — stacks on mobile, 3-across on lg+ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-7">
        {CHANNELS.map((channel) => (
          <ChannelCard key={channel.id} channel={channel} />
        ))}
      </div>
    </div>
  </section>
);

export default KnowledgeBase;
